import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Flex,
  FormControl,
  Text,
  HStack,
  Tooltip,
} from '@chakra-ui/react';
import { Formik, Form, getIn } from 'formik';
import React from 'react';

import { CreditAgreementFullArtifactWipConfigType } from '../../types';
import {
  FormFieldLabel,
  InputFormField,
  TextareaFormField,
} from 'packages/common-frontend/components/FormField';
import { METADATA_DISPLAY_FIELDS } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

type EditMetadataModalProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  wipConfig: CreditAgreementFullArtifactWipConfigType;
};
export const EditMetadataModal = ({
  isOpen,
  setIsOpen,
  wipConfig,
}: EditMetadataModalProps) => {
  const { formProps, isMutateAllowed } = wipConfig;

  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <ModalOverlay />
      <ModalContent padding="20px" minW="3xl" width="2xl">
        <ModalHeader>Edit Metadata</ModalHeader>
        <ModalCloseButton />
        <ModalBody width="100%">
          <Formik
            onSubmit={(props) => {
              for (const metadataFieldKey of METADATA_DISPLAY_FIELDS) {
                const currentProperty = getIn(
                  formProps.values,
                  metadataFieldKey,
                );
                const newProperty = getIn(props, metadataFieldKey);
                if (currentProperty !== newProperty) {
                  formProps.setFieldValue(metadataFieldKey, newProperty);
                }
              }

              wipConfig.saveCreditAgreementFullArtifactWip({
                variables: {
                  creditAgreementFullArtifactWip: {
                    id: formProps.values.id,
                    creditFacilityId: formProps.values.creditFacilityId,
                    creditAgreement: JSON.stringify(props),
                  },
                },
              });
              setIsOpen(false);
            }}
            initialValues={{ ...formProps.values }}
          >
            {(localFormProps) => (
              <Form onSubmit={localFormProps.handleSubmit}>
                <InputFormField
                  propName="id"
                  label="ID"
                  formProps={localFormProps}
                  value={localFormProps.values.id}
                  readOnly // always readonly
                  labelWidth="sm"
                  inputWidth="sm"
                  flexDirection="row"
                  alignItems="flex-end"
                />
                <InputFormField
                  propName="creditFacilityId"
                  label="Credit facility ID"
                  formProps={localFormProps}
                  value={localFormProps.values.creditFacilityId}
                  readOnly // always readonly
                  labelWidth="sm"
                  inputWidth="sm"
                  flexDirection="row"
                  alignItems="flex-end"
                  marginTop={5}
                />
                <InputFormField
                  propName="version"
                  label="Version"
                  formProps={localFormProps}
                  value={localFormProps.values.version}
                  labelWidth="sm"
                  inputWidth="sm"
                  flexDirection="row"
                  alignItems="flex-end"
                  marginTop={5}
                  readOnly={!isMutateAllowed('version')}
                />
                <Text fontStyle="italic" fontSize="sm" mt={1} color="gray.500">
                  The version has been incremented by 0.001 automatically, but
                  this can be explicitly set as well.
                </Text>
                <HStack marginTop={5}>
                  <Tooltip
                    hasArrow
                    label="If selected, a commit will set the effective from value to the time at commit.
              Otherwise, the effective from value will need to be manually set to a future date/time."
                  >
                    <InfoOutlineIcon />
                  </Tooltip>
                </HStack>
                <Flex alignItems="flex-end" mt={5} flexDirection="row">
                  <FormFieldLabel label="Effective from (ET)" labelWidth="60" />
                  <FormControl width="sm">
                    <InputFormField
                      isDisabled
                      propName="conditionalNow"
                      formProps={localFormProps}
                      readOnly // always readonly
                      value="now"
                      labelWidth="sm"
                      inputWidth="sm"
                      flexDirection="row"
                      alignItems="flex-end"
                    />
                  </FormControl>
                </Flex>
                <InputFormField
                  propName="effectiveTo"
                  label="Effective to (ET)"
                  formProps={localFormProps}
                  readOnly // always readonly
                  value="-"
                  labelWidth="sm"
                  inputWidth="sm"
                  flexDirection="row"
                  alignItems="flex-end"
                  marginTop={5}
                />
                <InputFormField
                  propName="_schemaVersion_"
                  label="Schema version"
                  formProps={localFormProps}
                  readOnly // always readonly
                  labelWidth="sm"
                  inputWidth="sm"
                  flexDirection="row"
                  alignItems="flex-end"
                  marginTop={5}
                />
                <TextareaFormField
                  propName="_note_"
                  label="Note"
                  formProps={localFormProps}
                  value={localFormProps.values._note_}
                  readOnly={!isMutateAllowed('_note_')}
                  labelWidth="sm"
                  inputWidth="sm"
                  flexDirection="row"
                  alignItems="flex-end"
                  marginTop={5}
                />
                <Flex justifyContent="center" mt="8">
                  <Button
                    type="submit"
                    aria-label="Done"
                    isDisabled={Object.keys(localFormProps.errors).length > 0}
                  >
                    Done
                  </Button>
                </Flex>
              </Form>
            )}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
